import {addDays, getDay, isBefore, startOfDay} from 'date-fns'

import {toApiDateString} from '../jobs/job-schedule-format'
import {createUuid} from '../lib/create-uuid'
import type {
    BuildOutRequirement,
    BuildOutScheduling,
    GeneratedJob,
    GeneratedJobStage,
    GeneratedRequirement,
    ServiceTemplateRequirement,
} from './build-out-types'
import {jsWeekdaysToPython} from './weekday-conversion'

function createTemplateRequirementId(): string {
    return createUuid()
}

export function addTemplateRequirement(
    template: ServiceTemplateRequirement[],
    kind: ServiceTemplateRequirement['kind'],
): ServiceTemplateRequirement[] {
    return [...template, {id: createTemplateRequirementId(), kind, typeId: '', qty: 1}]
}

export function removeTemplateRequirement(
    template: ServiceTemplateRequirement[],
    requirementId: string,
): ServiceTemplateRequirement[] {
    return template.filter((requirement) => requirement.id !== requirementId)
}

export function updateTemplateRequirementQty(
    template: ServiceTemplateRequirement[],
    requirementId: string,
    qty: number,
): ServiceTemplateRequirement[] {
    return template.map((requirement) =>
        requirement.id === requirementId ? {...requirement, qty: Math.max(1, qty)} : requirement,
    )
}

export function setTemplateRequirementType(
    template: ServiceTemplateRequirement[],
    requirementId: string,
    typeId: string,
): ServiceTemplateRequirement[] {
    return template.map((requirement) =>
        requirement.id === requirementId ? {...requirement, typeId} : requirement,
    )
}

function toGeneratedRequirement(requirement: BuildOutRequirement): GeneratedRequirement | null {
    const typeId =
        requirement.typeId ??
        (requirement.kind === 'worker' ? requirement.workerTypeId : requirement.rentalAssetTypeId)
    if (!typeId) {
        return null
    }

    return {
        kind: requirement.kind,
        typeId,
        qty: requirement.qty,
    }
}

function requirementsForStage(requirements: BuildOutRequirement[]): GeneratedRequirement[] {
    return requirements
        .map(toGeneratedRequirement)
        .filter((requirement): requirement is GeneratedRequirement => requirement != null)
}

function requirementsForOneDay(requirements: BuildOutRequirement[]): GeneratedRequirement[] {
    return requirementsForStage(requirements)
}

function templateToGeneratedRequirements(
    template: ServiceTemplateRequirement[],
): GeneratedRequirement[] {
    return template
        .filter((requirement) => requirement.typeId)
        .map((requirement) => ({
            kind: requirement.kind,
            typeId: requirement.typeId,
            qty: requirement.qty,
        }))
}

export function deriveServiceVisitDates(
    startDate: Date,
    endDate: Date,
    cadence: number[] | {weekdays: number[]},
    options: {visitsOnly?: boolean} = {},
): Date[] {
    const cadenceWeekdays = Array.isArray(cadence) ? cadence : cadence.weekdays
    const deploy = startOfDay(startDate)
    const pickup = startOfDay(endDate)
    const visits: Date[] = []
    // Without deploy and pickup jobs the date range is inclusive.
    const lastVisit = options.visitsOnly ? addDays(pickup, 1) : pickup

    if (isBefore(lastVisit, deploy) || (!options.visitsOnly && !isBefore(deploy, pickup))) {
        return visits
    }
    if (cadenceWeekdays.length === 0) {
        return visits
    }

    const weekdays = new Set(cadenceWeekdays)

    let cursor = options.visitsOnly ? deploy : addDays(deploy, 1)
    while (isBefore(cursor, lastVisit)) {
        if (weekdays.has(getDay(cursor))) {
            visits.push(new Date(cursor))
        }
        cursor = addDays(cursor, 1)
    }

    return visits
}

export function isSeveralDays(scheduling: Pick<BuildOutScheduling, 'pattern' | 'severalDays'>) {
    return scheduling.pattern === 'one_day' && scheduling.severalDays === true
}

export const SEVERAL_DAYS_LABEL = 'Several days in a row'

export function buildDraftJobs(
    scheduling: BuildOutScheduling,
    lineRequirements: BuildOutRequirement[],
): GeneratedJob[] {
    const pickupDate = scheduling.endDate
    const skippedDates = new Set(scheduling.skippedServiceDates)

    if (isSeveralDays(scheduling)) {
        return [
            {
                id: 'several-days-0',
                stage: 'deploy',
                date: scheduling.startDate,
                endDate: scheduling.endDate,
                label: SEVERAL_DAYS_LABEL,
                requirements: requirementsForOneDay(lineRequirements),
            },
        ]
    }

    if (scheduling.pattern === 'one_day') {
        return [
            {
                id: 'one-day-0',
                stage: 'deploy',
                date: scheduling.startDate,
                label: 'One-day job',
                requirements: requirementsForOneDay(lineRequirements),
            },
        ]
    }

    if (scheduling.pattern === 'deploy_pickup') {
        return [
            {
                id: 'deploy-0',
                stage: 'deploy',
                date: scheduling.startDate,
                label: 'Deployment',
                requirements: requirementsForStage(lineRequirements),
            },
            {
                id: 'pickup-0',
                stage: 'pickup',
                date: pickupDate,
                label: 'Pickup',
                requirements: requirementsForStage(lineRequirements),
            },
        ]
    }

    const visitDates = deriveServiceVisitDates(
        scheduling.startDate,
        pickupDate,
        scheduling.cadence.weekdays,
        {visitsOnly: scheduling.visitsOnly},
    )
    const serviceRequirements = templateToGeneratedRequirements(scheduling.serviceTemplate)
    const visits: GeneratedJob[] = visitDates.map((date, visitIndex) => ({
        id: `service-${visitIndex}`,
        stage: 'service' as const,
        date,
        label: 'Service visit',
        visitIndex,
        requirements: serviceRequirements,
        skipped: skippedDates.has(toApiDateString(date)),
    }))
    if (scheduling.visitsOnly) {
        return visits
    }

    const jobs: GeneratedJob[] = [
        {
            id: 'deploy-0',
            stage: 'deploy',
            date: scheduling.startDate,
            label: 'Deployment',
            requirements: requirementsForStage(lineRequirements),
        },
        ...visitDates.map((date, visitIndex) => ({
            id: `service-${visitIndex}`,
            stage: 'service' as const,
            date,
            label: 'Service visit',
            visitIndex,
            requirements: serviceRequirements,
            skipped: skippedDates.has(toApiDateString(date)),
        })),
        {
            id: 'pickup-0',
            stage: 'pickup',
            date: pickupDate,
            label: 'Pickup',
            requirements: requirementsForStage(lineRequirements),
        },
    ]

    return jobs.sort((left, right) => left.date.getTime() - right.date.getTime())
}

/** Names jobs of each stage get when nobody typed one. */
export const DEFAULT_STAGE_JOB_NAMES: Record<GeneratedJobStage, string> = {
    deploy: 'Deployment',
    service: 'Service visit',
    pickup: 'Pickup',
}

type StageTimes = {startTime: string | null; endTime: string | null}

/** The name typed into a stage's name field, empty when it has not been set. */
export function stageJobNameValue(
    scheduling: BuildOutScheduling,
    stage: GeneratedJobStage,
): string {
    if (stage === 'deploy') {
        return scheduling.deployJobName ?? ''
    }
    if (stage === 'pickup') {
        return scheduling.pickupJobName ?? ''
    }

    return scheduling.serviceJobName ?? ''
}

/**
 * The name jobs of a stage are created with. A one-day build-out has no stage default — its single
 * job is named by the wizard's job name field.
 */
export function resolveStageJobName(
    scheduling: BuildOutScheduling,
    stage: GeneratedJobStage,
): string {
    const typed = stageJobNameValue(scheduling, stage).trim()
    if (typed) {
        return typed
    }

    return scheduling.pattern === 'one_day' ? '' : DEFAULT_STAGE_JOB_NAMES[stage]
}

export function setStageJobName(
    scheduling: BuildOutScheduling,
    stage: GeneratedJobStage,
    name: string,
): BuildOutScheduling {
    if (stage === 'deploy') {
        return {...scheduling, deployJobName: name}
    }
    if (stage === 'pickup') {
        return {...scheduling, pickupJobName: name}
    }

    return {...scheduling, serviceJobName: name}
}

/** Deploy and pickup fall back to the service times when they have none of their own. */
export function resolveStageTimes(
    scheduling: BuildOutScheduling,
    stage: GeneratedJobStage,
): StageTimes {
    const sharedStart = scheduling.startTime ?? null
    const sharedEnd = scheduling.endTime ?? null

    if (stage === 'deploy') {
        return {
            startTime: scheduling.deployStartTime ?? sharedStart,
            endTime: scheduling.deployEndTime ?? sharedEnd,
        }
    }
    if (stage === 'pickup') {
        return {
            startTime: scheduling.pickupStartTime ?? sharedStart,
            endTime: scheduling.pickupEndTime ?? sharedEnd,
        }
    }

    return {startTime: sharedStart, endTime: sharedEnd}
}

export function setStageTimes(
    scheduling: BuildOutScheduling,
    stage: GeneratedJobStage,
    times: Partial<StageTimes>,
): BuildOutScheduling {
    const next = {...resolveStageTimes(scheduling, stage), ...times}

    if (stage === 'deploy') {
        return {...scheduling, deployStartTime: next.startTime, deployEndTime: next.endTime}
    }
    if (stage === 'pickup') {
        return {...scheduling, pickupStartTime: next.startTime, pickupEndTime: next.endTime}
    }

    return {...scheduling, startTime: next.startTime, endTime: next.endTime}
}

export function buildSummaryCounts(jobs: GeneratedJob[]): {
    jobs: number
    units: number
    crew: number
} {
    const equipmentTypeIds = new Set<string>()
    let peakCrew = 0

    for (const job of jobs) {
        let jobCrew = 0

        for (const requirement of job.requirements) {
            if (requirement.kind === 'worker') {
                jobCrew += requirement.qty
            } else {
                equipmentTypeIds.add(requirement.typeId)
            }
        }

        peakCrew = Math.max(peakCrew, jobCrew)
    }

    return {
        jobs: jobs.length,
        units: equipmentTypeIds.size,
        crew: peakCrew,
    }
}

export function toggleSkippedServiceDate(
    scheduling: BuildOutScheduling,
    serviceDate: Date,
): BuildOutScheduling {
    const dateKey = toApiDateString(serviceDate)
    const skipped = new Set(scheduling.skippedServiceDates)
    if (skipped.has(dateKey)) {
        skipped.delete(dateKey)
    } else {
        skipped.add(dateKey)
    }

    return {
        ...scheduling,
        skippedServiceDates: [...skipped].sort(),
    }
}

export function schedulingCadenceForApi(scheduling: BuildOutScheduling): number[] {
    return jsWeekdaysToPython(scheduling.cadence.weekdays)
}

export function templateRequirementsToApi(template: ServiceTemplateRequirement[]) {
    return {
        serviceWorkerRequirements: template
            .filter((requirement) => requirement.kind === 'worker' && requirement.typeId)
            .map((requirement) => ({
                workerTypeId: requirement.typeId,
                quantity: requirement.qty,
            })),
        serviceAssetRequirements: template
            .filter((requirement) => requirement.kind === 'equipment' && requirement.typeId)
            .map((requirement) => ({
                rentalAssetTypeId: requirement.typeId,
                quantity: requirement.qty,
            })),
    }
}

export function quoteRequirementsToStageInput(requirements: BuildOutRequirement[]) {
    return {
        workerRequirements: requirements
            .filter(
                (requirement) => requirement.kind === 'worker' && requirement.workerTypeId != null,
            )
            .map((requirement) => ({
                workerTypeId: requirement.workerTypeId!,
                quantity: requirement.qty,
            })),
        assetRequirements: requirements
            .filter(
                (requirement) =>
                    requirement.kind === 'equipment' && requirement.rentalAssetTypeId != null,
            )
            .map((requirement) => ({
                rentalAssetTypeId: requirement.rentalAssetTypeId!,
                quantity: requirement.qty,
            })),
        notes: null,
    }
}
